import { useState, useEffect } from "react";
import { Button } from "@material-tailwind/react";
import { useCart } from "@shopify/hydrogen-react";

interface ProductVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  availableForSale: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

interface AddToCartSectionProps {
  selectedVariant: ProductVariant | null;
  quantity?: number;
  onAddToCartSuccess?: () => void;
}

export default function AddToCartSection({
  selectedVariant,
  quantity: externalQuantity,
  onAddToCartSuccess,
}: AddToCartSectionProps) {
  const { linesAdd, status } = useCart();
  const [internalQuantity, setInternalQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  // Use external quantity if provided, otherwise use internal state
  const quantity = externalQuantity ?? internalQuantity;
  const shouldShowQuantitySelector = externalQuantity === undefined;

  useEffect(() => {
    if (externalQuantity !== undefined) {
      setInternalQuantity(externalQuantity);
    }
  }, [externalQuantity]);

  const handleAddToCart = async () => {
    if (!selectedVariant || !selectedVariant.availableForSale) return;

    setAddingToCart(true);

    try {
      await linesAdd([
        {
          merchandiseId: selectedVariant.id,
          quantity: quantity,
        },
      ]);

      onAddToCartSuccess?.();
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setAddingToCart(false);
    }
  };

  const isVariantAvailable =
    selectedVariant && selectedVariant.availableForSale;

  return (
    <div className="space-y-4">
      {/* Quantity Selector - only show if quantity is managed internally */}
      {shouldShowQuantitySelector && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                setInternalQuantity(Math.max(1, internalQuantity - 1))
              }
              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
            >
              -
            </button>
            <span className="w-12 text-center">{quantity}</span>
            <button
              onClick={() => setInternalQuantity(internalQuantity + 1)}
              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Add to Cart Button */}
      <Button
        onClick={handleAddToCart}
        disabled={
          !isVariantAvailable ||
          addingToCart ||
          status === "creating" ||
          status === "updating"
        }
        className={`w-full py-3 ${
          isVariantAvailable && status !== "creating" && status !== "updating"
            ? "bg-scpb-light-blue hover:bg-blue-700 rounded-md"
            : "bg-gray-300 cursor-not-allowed rounded-md"
        }`}
      >
        {addingToCart || status === "creating" || status === "updating"
          ? "Adding to Cart..."
          : isVariantAvailable
          ? "Add to Cart"
          : "Out of Stock"}
      </Button>
    </div>
  );
}
